import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import { Plus, X, Radio, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function LiveRequestsPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preTask = params.get("task");
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [show, setShow] = useState(!!preTask);
  const [responding, setResponding] = useState(null);

  const load = () => api.get("/live-requests").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/tasks").then((r) => setTasks(r.data));
  }, []);

  const canRespond = user.role !== "engineer";

  return (
    <div>
      <PageHeader
        eyebrow="Field Radio"
        title="Live Requests"
        subtitle={user.role === "engineer" ? "Request material, support or permission from the field." : "Real-time requests from field engineers."}
        right={<button onClick={() => setShow(true)} className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="new-live-req-btn"><Plus className="w-4 h-4" /> New Request</button>}
      />

      <Card>
        {items.length === 0 ? <EmptyState title="No live requests yet" /> : (
          <div className="divide-y divide-white/10">
            {items.map((r) => (
              <div key={r.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`lr-row-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Radio className="w-4 h-4 text-[#FF5722]" />
                    <span className="font-medium">{r.title}</span>
                    <StatusPill status={r.status} size="xs" />
                    <span className="mono text-[10px] uppercase text-slate-500">{r.priority}</span>
                  </div>
                  <div className="text-sm text-slate-400">{r.description}</div>
                  <div className="mono text-xs text-slate-500 mt-1">{r.engineer_name} · {new Date(r.created_at).toLocaleString()}</div>
                  {r.response && (
                    <div className="mt-3 border-l-2 border-[#FF5722] pl-3">
                      <div className="mono text-[10px] uppercase tracking-widest text-[#FF5722]">Response</div>
                      <div className="text-sm text-slate-200">{r.response}</div>
                    </div>
                  )}
                </div>
                {canRespond && r.status !== "closed" && (
                  <button onClick={() => setResponding(r)} className="border border-white/15 hover:border-[#FF5722] text-xs px-3 py-2 mono uppercase" data-testid={`lr-respond-${r.id}`}>
                    Respond
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {show && <NewDialog tasks={tasks} preTask={preTask} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
      {responding && <RespondDialog req={responding} onClose={() => setResponding(null)} onDone={() => { setResponding(null); load(); }} />}
    </div>
  );
}

function NewDialog({ tasks, preTask, onClose, onCreated }) {
  const [form, setForm] = useState({
    request_type: "material",
    title: "",
    description: "",
    task_id: preTask || "",
    priority: "medium",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Enter a title");
    setBusy(true);
    try {
      await api.post("/live-requests", form);
      toast.success("Request sent");
      onCreated();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Live Request</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type">
              <select className="input" value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} data-testid="lr-type">
                <option value="material">Material</option>
                <option value="support">Support</option>
                <option value="permission">Permission</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Priority">
              <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="lr-priority">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>
          <Field label="Related Task">
            <select className="input" value={form.task_id} onChange={(e) => setForm({ ...form, task_id: e.target.value })}>
              <option value="">— None —</option>
              {tasks.map((t) => <option key={t.id} value={t.id}>{t.task_serial} · {t.site_name || t.job_id}</option>)}
            </select>
          </Field>
          <Field label="Title"><input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lr-title" /></Field>
          <Field label="Description"><textarea rows={3} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lr-desc" /></Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="lr-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Send className="w-4 h-4" /> Send
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}

function RespondDialog({ req, onClose, onDone }) {
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("in_progress");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/live-requests/${req.id}`, { response, status });
      toast.success("Response sent");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Respond</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="mono text-[10px] text-slate-500 uppercase">Original</div>
            <div className="font-medium">{req.title}</div>
            <div className="text-sm text-slate-400 mt-1">{req.description}</div>
          </div>
          <Field label="Status">
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lr-resp-status">
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </Field>
          <Field label="Response"><textarea rows={4} className="input" value={response} onChange={(e) => setResponse(e.target.value)} data-testid="lr-resp-text" /></Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="lr-resp-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}
